import React, { useEffect, useRef } from "react";
import Modal from "@mui/material/Modal";

const OtpVerificationModal = ({
  isOpen,
  onClose,
  otp,
  handleOtpChange,
  handleKeyDown,
  firstInputRef,
  isVerified,
  handleVerifyOtp,
  otpError,
}) => {
  const modalRef = useRef(null);
  const internalInputRef = useRef(null);

  // Use internal ref if external ref is not provided
  const actualFirstInputRef = firstInputRef || internalInputRef;

  useEffect(() => {
    if (isOpen) {
      // Focus the first input when modal opens
      const focusFirstInput = () => {
        if (actualFirstInputRef.current) {
          actualFirstInputRef.current.focus();
        }
      };

      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(focusFirstInput, 300);

      return () => clearTimeout(timer);
    }
  }, [isOpen, actualFirstInputRef]);

  // Reset and focus first input when error occurs
  useEffect(() => {
    if (otpError && isOpen) {
      const timer = setTimeout(() => {
        if (actualFirstInputRef.current) {
          actualFirstInputRef.current.focus();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [otpError, isOpen, actualFirstInputRef]);

  // Add global keydown listener for Enter key
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter" && isVerified && !otpError) {
        handleVerifyOtp();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyPress);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [isOpen, isVerified, otpError, handleVerifyOtp]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="otp-modal-title"
      aria-describedby="otp-modal-description"
    >
      <div
        ref={modalRef}
        style={{
          background: "#fff",
          padding: "2rem",
          borderRadius: "8px",
          width: "400px",
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          textAlign: "center",
        }}
      >
        <p id="otp-modal-title" className="text-2xl font-semibold mb-5">
          Verify OTP
        </p>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "3px",
            gap: "8px",
          }}
        >
          {[...Array(6)].map((_, index) => (
            <input
              key={index}
              id={`otp-input-${index}`}
              type="text"
              maxLength="1"
              value={otp[index] || ""}
              onChange={(e) => handleOtpChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              ref={index === 0 ? actualFirstInputRef : null}
              className={`w-12 h-12 ${
                otpError ? "text-red-600" : "text-var(--heading-color)"
              } border-2 rounded text-center text-lg font-semibold focus:outline-none focus:ring-2 ${
                otpError
                  ? "border-red-500 focus:ring-red-500"
                  : "border-blue-300 focus:ring-blue-500"
              }`}
              style={{
                flex: "1",
                maxWidth: "48px",
              }}
              autoFocus={index === 0 && isOpen} // Auto-focus only when modal is open
            />
          ))}
        </div>

        <button
          style={{
            backgroundColor:
              isVerified && !otpError
                ? "var(--primary-color)"
                : "var(--light-grey-color)",
            color:
              isVerified && !otpError
                ? "var(--secondary-color)"
                : "var(--heading-color)",
            fontWeight: "600",
            marginTop: "2.5rem",
            cursor: isVerified && !otpError ? "pointer" : "not-allowed",
          }}
          className="w-full text-white font-semibold py-2 rounded-lg mb-4 mt-5"
          disabled={!isVerified || otpError}
          onClick={handleVerifyOtp}
        >
          Verify
        </button>

        {otpError && <p className="text-red-600 text-sm mt-2">{otpError}</p>}
      </div>
    </Modal>
  );
};

export default OtpVerificationModal;
